import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Building2,
  FileText,
  Home,
  LifeBuoy,
  LogOut,
  Megaphone,
  Menu,
  UserRound,
  Users,
  WalletCards,
  MessageSquareMore,
  CalendarDays,
  FileCheck2,
  Shield,
  CalendarCheck2,
  ParkingSquare,
  Wrench,
} from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo, Loading } from "./shared";
import { Button } from "./ui/button";

const NAV_ITEMS = [
  { path: "/resident", Icon: Home, label: "Home", testId: "resident-nav-home" },
  {
    path: "/resident/society",
    Icon: Building2,
    label: "My Society",
    testId: "resident-nav-society",
  },
  {
    path: "/resident/units",
    Icon: Building2,
    label: "My Units",
    testId: "resident-nav-units",
  },
  {
    path: "/resident/household",
    Icon: Users,
    label: "My Household",
    testId: "resident-nav-household",
  },
  {
    path: "/resident/finance",
    Icon: WalletCards,
    label: "Bills & Payments",
    testId: "resident-nav-finance",
    feature: "sm03_billing",
  },
  {
    path: "/resident/helpdesk",
    Icon: MessageSquareMore,
    label: "Complaints",
    testId: "resident-nav-helpdesk",
    feature: "sm05_helpdesk",
  },
  {
    path: "/resident/doorstep-services",
    Icon: Wrench,
    label: "Doorstep Services",
    testId: "resident-nav-doorstep-services",
  },
  {
    path: "/resident/notices",
    Icon: Megaphone,
    label: "Notices",
    testId: "resident-nav-notices",
  },
  {
    path: "/resident/meetings",
    Icon: CalendarDays,
    label: "Meetings",
    testId: "resident-nav-meetings",
    feature: "rp06_meetings",
  },
  {
    path: "/resident/requests",
    Icon: FileCheck2,
    label: "Requests & NOC",
    testId: "resident-nav-requests",
    feature: "rp07_formal_requests",
  },
  {
    path: "/resident/visitor-approvals",
    Icon: Shield,
    label: "Visitors",
    testId: "resident-nav-visitor-approvals",
    feature: "rp08_gate_security",
  },
  {
    path: "/resident/amenities",
    Icon: CalendarCheck2,
    label: "Amenities",
    testId: "resident-nav-amenities",
    feature: "rp09_amenity_booking",
  },
  {
    path: "/resident/parking",
    Icon: ParkingSquare,
    label: "Parking & Vehicles",
    testId: "resident-nav-parking",
    feature: "rp10_parking_vehicles",
  },
  {
    path: "/resident/documents",
    Icon: FileText,
    label: "Documents",
    testId: "resident-nav-documents",
  },
  {
    path: "/resident/profile",
    Icon: UserRound,
    label: "My Profile",
    testId: "resident-nav-profile",
  },
];

export default function ResidentLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [context, setContext] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api
      .get("/resident/context")
      .then((response) => setContext(response.data))
      .catch(() => {
        navigate("/login", { replace: true });
      });
  }, [navigate]);

  const switchSociety = async (societyId) => {
    if (
      !context?.societies?.some((society) => society.society_id === societyId)
    )
      return;
    await api.post("/resident/context", { society_id: societyId });
    window.location.assign("/resident");
  };

  const switchUnit = async (unitId) => {
    if (!context?.units?.some((unit) => unit.unit_id === unitId)) return;
    await api.post("/resident/context", { unit_id: unitId });
    window.location.assign(window.location.pathname);
  };

  if (!context) return <Loading />;

  const features = context.features || {};
  const visibleNav = NAV_ITEMS.filter((item) => {
    if (item.path === "/resident/finance" && !context.active_unit_finance_access) {
      return false;
    }
    return !item.feature || features[item.feature] || item.path === "/resident/finance";
  });

  return (
    <div
      className="min-h-screen bg-slate-50"
      data-testid="resident-portal-shell"
    >
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-2 px-3 sm:px-4">
          <Logo />
          <div className="flex min-w-0 items-center gap-2">
            <select
              className="min-w-0 max-w-[120px] truncate rounded-md border border-slate-200 bg-white px-2 py-2 text-sm sm:max-w-[170px]"
              data-testid="resident-society-switcher"
              onChange={(event) => switchSociety(event.target.value)}
              value={context.active_society_id}
            >
              {context.societies.map((society) => (
                <option key={society.society_id} value={society.society_id}>
                  {society.name}
                </option>
              ))}
            </select>
            {context.units?.length > 1 ? (
              <select
                className="min-w-0 max-w-[120px] truncate rounded-md border border-slate-200 bg-white px-2 py-2 text-sm sm:max-w-[170px]"
                data-testid="resident-unit-switcher"
                onChange={(event) => switchUnit(event.target.value)}
                value={context.active_unit_id}
              >
                {context.units.map((unit) => (
                  <option key={unit.unit_id} value={unit.unit_id}>
                    Unit {unit.unit_number}
                  </option>
                ))}
              </select>
            ) : (
              <span
                className="hidden max-w-[150px] truncate text-sm font-medium text-slate-700 sm:inline"
                data-testid="resident-active-unit-label"
              >
                {context.units?.[0] ? `Unit ${context.units[0].unit_number}` : "My unit"}
              </span>
            )}
            <Button
              className="lg:hidden"
              data-testid="resident-menu-toggle"
              onClick={() => setMenuOpen((open) => !open)}
              size="icon"
              variant="outline"
            >
              <Menu />
            </Button>
            <Button
              className="hidden sm:inline-flex"
              data-testid="resident-logout-button"
              onClick={logout}
              variant="ghost"
            >
              <LogOut />
              Sign out
            </Button>
            <Button
              className="sm:hidden"
              data-testid="resident-logout-button-mobile"
              onClick={logout}
              size="icon"
              variant="ghost"
            >
              <LogOut />
            </Button>
          </div>
        </div>
      </header>
      <div className="mx-auto flex max-w-6xl">
        <aside
          className={`${menuOpen ? "block" : "hidden"} w-full border-b border-slate-200 bg-white p-3 lg:block lg:min-h-[calc(100vh-4rem)] lg:w-56 lg:border-b-0 lg:border-r`}
          data-testid="resident-navigation"
        >
          <nav className="flex flex-wrap gap-2 lg:flex-col">
            {visibleNav.map(({ path, Icon, label, testId }) => (
              <NavLink
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
                data-testid={testId}
                end={path === "/resident"}
                key={path}
                onClick={() => setMenuOpen(false)}
                to={path}
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
            {features.sm05_helpdesk && (
              <NavLink
                className={({ isActive }) =>
                  `mt-2 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
                data-testid="resident-nav-help"
                onClick={() => setMenuOpen(false)}
                to="/resident/helpdesk"
              >
                <LifeBuoy className="h-4 w-4" />
                Need Help / Feedback
              </NavLink>
            )}
          </nav>
        </aside>
        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet context={{ context }} />
        </main>
      </div>
    </div>
  );
}
